import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getPaymentStatus } from '../services/api';

function PaymentError() {
  const { paymentId, merchantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const error = searchParams.get('error');

  const fetchPaymentStatus = async () => {
    try {
      const response = await getPaymentStatus(paymentId);
      setPayment(response.data);
    } catch (error) {
      console.error('Error fetching payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paymentId) {
      fetchPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [paymentId]);

  const getErrorMessage = () => {
    switch (error) {
      case 'missing_transaction_id':
        return '缺少交易編號，無法完成付款確認。';
      case 'transaction_id_mismatch':
        return '交易編號不匹配，付款驗證失敗。';
      case 'confirmation_failed':
        return 'LINE Pay 付款確認失敗，請聯繫客服。';
      case 'server_error':
        return '伺服器發生錯誤，請稍後再試。';
      default:
        return '付款過程中發生錯誤，請稍後再試或聯繫客服。';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-primary text-lg">載入中...</div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6">
      {/* Error Icon */}
      <div className="flex flex-col items-center gap-4 p-6 bg-white dark:bg-background-dark rounded-xl shadow-sm mb-6">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30">
          <span className="material-symbols-outlined text-red-500 text-5xl">error</span>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark mb-2">
            付款失敗
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {getErrorMessage()}
          </p>
        </div>
      </div>

      {/* Payment Details (if available) */}
      {payment && (
        <div className="bg-white dark:bg-background-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark mb-4">付款資訊</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">付款金額</span>
              <span className="text-text-light dark:text-text-dark font-medium">NT$ {payment.amount}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">付款狀態</span>
              <span className="text-red-600 dark:text-red-400 font-medium">
                {payment.status === 'failed' ? '失敗' : 
                 payment.status === 'cancelled' ? '已取消' : '處理中'}
              </span>
            </div>
            
            {payment.transaction_id && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">交易編號</span>
                <span className="text-text-light dark:text-text-dark font-mono text-sm">{payment.transaction_id}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {payment && (
          <Link
            to={`/payment/${payment.booking_id}`}
            className="w-full h-12 flex items-center justify-center bg-primary text-white text-base font-bold rounded-lg hover:bg-primary/90 transition-colors"
          >
            重新付款
          </Link>
        )}
        
        {payment && (
          <Link
            to={`/confirmation/${payment.booking_id}`}
            className="w-full h-12 flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-text-light dark:text-text-dark text-base font-bold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            查看體驗詳情
          </Link>
        )}
        
        <Link
          to="/bookings"
          className="w-full h-12 flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-text-light dark:text-text-dark text-base font-bold rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          查看所有體驗
        </Link>
        
        <Link
          to={`/${merchantSlug}`}
          className="w-full h-12 flex items-center justify-center text-primary text-base font-bold rounded-lg hover:bg-primary/10 transition-colors"
        >
          返回首頁
        </Link>
      </div>

      {/* Help Section */}
      <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <div className="flex items-start gap-2">
          <span className="material-symbols-outlined text-yellow-500 mt-0.5">help</span>
          <div>
            <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">需要協助？</p>
            <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
              如果您在付款過程中遇到問題，請聯繫我們的客服團隊，我們將盡快為您處理。
            </p>
            <div className="mt-2">
              <a 
                href="tel:+886-2-1234-5678" 
                className="text-xs text-yellow-700 dark:text-yellow-400 underline"
              >
                客服電話: (02) 1234-5678
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentError;